import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ArrowLeft, ShoppingBag, Gift } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { formatMoney, originalPrice, bogoTier } from "../lib/apiClient";

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal, trackAbandoned, freeGift, bogo, saleOn } = useCart();
  const navigate = useNavigate();

  const FREE_SHIP = 200;
  const remaining = Math.max(0, FREE_SHIP - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIP) * 100);
  const bogoDiscount = bogo ? Math.round(items.reduce((s, i) => s + i.price * i.quantity * bogoTier(i.quantity).pct, 0) * 100) / 100 : 0;
  const bogoFreeCount = bogo ? items.reduce((s, i) => s + bogoTier(i.quantity).free, 0) : 0;
  const giftEligible = !!(freeGift && subtotal >= freeGift.threshold);
  const giftName = freeGift ? freeGift.gift.name.replace(/\s*\(COA VERIFIED\)/i, "") : "";
  const giftRemaining = freeGift ? Math.max(0, freeGift.threshold - subtotal) : 0;
  const giftProgress = freeGift ? Math.min(100, (subtotal / freeGift.threshold) * 100) : 0;

  const handleClose = (open) => {
    if (!open && items.length) {
      trackAbandoned({ source: "drawer", payment_intent_status: "Abandoned at Cart" });
    }
    setDrawerOpen(open);
  };

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={handleClose}>
      <SheetContent side="right" data-testid="cart-drawer" className="w-full sm:max-w-md bg-[#f4f1e9] border-l border-border flex flex-col p-0">
        <SheetHeader className="p-6 border-b border-border">
          <SheetTitle className="font-display font-bold text-xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Your Cart
          </SheetTitle>
          <SheetDescription className="sr-only">Review the research materials in your cart and proceed to checkout.</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
            <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">Your cart is empty.</p>
            <button onClick={() => setDrawerOpen(false)} data-testid="cart-continue-shopping" className="pill px-5 py-2.5 btn-ink text-sm font-semibold">
              Continue shopping
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 pt-4">
              <div className="text-xs text-muted-foreground mb-2">
                {remaining > 0 ? `Add ${formatMoney(remaining)} for free US shipping` : "🎉 You unlocked free US shipping"}
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: "var(--gold)" }} />
              </div>
            </div>

            {freeGift && (
              <div className="px-6 pt-3" data-testid="cart-gift-nudge">
                <div className="text-xs mb-2 flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5 text-[#8a681c] shrink-0" />
                  {giftRemaining > 0 ? (
                    <span className="text-muted-foreground" data-testid="cart-gift-nudge-text">
                      Add <span className="font-semibold text-foreground">{formatMoney(giftRemaining)}</span> more to unlock a free {giftName}
                    </span>
                  ) : (
                    <span className="font-semibold text-[#8a681c]" data-testid="cart-gift-nudge-unlocked">🎁 You unlocked a free {giftName}!</span>
                  )}
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full transition-all duration-500" style={{ width: `${giftProgress}%`, background: "linear-gradient(90deg,#c59b27,#e6c665)" }} />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 no-scrollbar">
              {items.map((i) => (
                <div key={i.key || i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-3 card-soft rounded-2xl p-3">
                  <img src={i.image_url} alt={i.name} className="w-16 h-16 rounded-xl object-cover bg-secondary" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{i.name}</p>
                    {i.variant && <p className="text-xs text-muted-foreground">{i.variant}</p>}
                    <p className="text-sm mt-0.5">
                      {saleOn && <span className="text-muted-foreground line-through mr-1.5 text-xs">{formatMoney(originalPrice(i.price))}</span>}
                      <span className="font-display font-bold">{formatMoney(i.price)}</span>
                    </p>
                    {bogo && bogoTier(i.quantity).pct > 0 && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8a681c] mt-1" data-testid={`cart-bogo-${i.product_id}`}>
                        {Math.round(bogoTier(i.quantity).pct * 100)}% off applied
                      </span>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => updateQty(i.key, i.quantity - 1)} data-testid={`cart-decrease-${i.product_id}`} className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center hover:bg-secondary/60">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm w-6 text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.key, i.quantity + 1)} data-testid={`cart-increase-${i.product_id}`} className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center hover:bg-secondary/60">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.product_id}`} className="text-muted-foreground hover:text-destructive self-start">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {bogo && bogoFreeCount > 0 && (
                <div className="rounded-2xl border border-[#c59b27]/50 bg-[#fdf6e3] p-3" data-testid="cart-free-card">
                  <p className="text-[11px] font-display font-bold uppercase tracking-wide text-[#8a681c] flex items-center gap-1 mb-2"><Gift className="w-3.5 h-3.5" /> Free with BOGO</p>
                  <div className="space-y-2">
                    {items.map((i) => {
                      const t = bogoTier(i.quantity);
                      if (t.free <= 0) return null;
                      return (
                        <div key={`cf-${i.product_id}`} className="flex items-center gap-3" data-testid={`cart-free-${i.product_id}`}>
                          <img src={i.image_url} alt={i.name} className="w-12 h-12 rounded-xl object-cover bg-secondary ring-1 ring-[#c59b27]/40" />
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{i.name}</p><p className="text-[11px] text-[#8a681c] font-semibold">Complimentary · Qty {t.free}</p></div>
                          <span className="text-sm font-bold text-[#8a681c]">FREE</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {giftEligible && (
                <div data-testid="cart-free-gift" className="flex gap-3 rounded-2xl p-3 border border-[#c59b27]/50 bg-[#fdf6e3]">
                  <img src={freeGift.gift.image_url} alt={freeGift.gift.name} className="w-16 h-16 rounded-xl object-cover bg-secondary" />
                  <div className="flex-1 min-w-0">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#8a681c] bg-[#f4e7c4] rounded-full px-2 py-0.5"><Gift className="w-3 h-3" /> Free gift</span>
                    <p className="text-sm font-semibold truncate mt-1">{freeGift.gift.name}</p>
                    <p className="text-sm mt-0.5 font-display font-bold text-[#8a681c]" data-testid="cart-free-gift-price">FREE</p>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-border p-6 space-y-4">
              <button onClick={() => setDrawerOpen(false)} data-testid="cart-continue-shopping-btn" className="w-full h-11 rounded-xl btn-outline-gold font-semibold flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Continue shopping
              </button>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-display font-bold text-xl" data-testid="cart-subtotal">{formatMoney(subtotal)}</span>
              </div>
              {bogo && bogoFreeCount > 0 && (
                <div className="flex items-center justify-between text-sm text-[#8a681c] font-semibold" data-testid="cart-bogo-summary">
                  <span className="flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> {bogoFreeCount} free vial{bogoFreeCount > 1 ? "s" : ""} added</span>
                  {bogoDiscount > 0 && <span data-testid="cart-bogo-savings">−{formatMoney(bogoDiscount)}</span>}
                </div>
              )}
              <button onClick={goCheckout} data-testid="checkout-button" className="w-full h-12 rounded-xl btn-ink font-display font-bold flex items-center justify-center gap-2 group">
                Checkout <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
